import React, { FC } from 'react';
import { toTitleCase } from 'src/shared/helpers';
import styled from 'styled-components';
import { Breadcumb, type BreadcumbExtraItem } from '../buttons/breadcumb';
import { PageHeaderContainer } from './pageHeaderContainer';
import { PageHeaderStar } from './pageHeaderStar';

interface IHeaderProps {
    title: React.ReactNode;
    description?: React.ReactNode;
    onBackClick?: () => void;
    backLabel?: React.ReactNode;
    pageKey?: string;
    breadcumbCurrentLabel?: React.ReactNode;
    breadcumbExtraItems?: BreadcumbExtraItem[];
    hideBreadcumb?: boolean;
    isBackVisible?: boolean;
    steps?: React.ReactNode;
    actions?: React.ReactNode;
    learnMoreLabel?: string;
    learnMoreUrl?: string;
    onLearnMoreClick?: () => void;
    compact?: boolean;
    hideStar?: boolean;
}

const _PageHeader: FC<IHeaderProps> = ({
    title,
    description,
    onBackClick,
    backLabel,
    pageKey,
    breadcumbCurrentLabel,
    breadcumbExtraItems,
    hideBreadcumb,
    isBackVisible = true,
    steps,
    actions: action,
    learnMoreLabel,
    learnMoreUrl,
    onLearnMoreClick,
    compact,
    hideStar
}) => {
    const handleLearnMoreClick = onLearnMoreClick
        ? (e: React.MouseEvent) => {
              e.preventDefault();
              onLearnMoreClick();
          }
        : undefined;
    // Page titles for tool pages follow the same title-case rules as the left menu (minor words stay
    // lowercase). Only plain-string titles are normalized; custom ReactNode titles are left as-is.
    const displayTitle = typeof title === 'string' ? toTitleCase(title) : title;
    if (compact) {
        return (
            <PageHeaderContainer.CompactWrap>
                <PageHeaderContainer.CompactRow>
                    {isBackVisible && !hideBreadcumb && (
                        <Breadcumb pageKey={pageKey} currentLabel={breadcumbCurrentLabel} extraItems={breadcumbExtraItems} onClick={onBackClick}>
                            {backLabel}
                        </Breadcumb>
                    )}
                    {title && (
                        <PageHeaderContainer.Title>
                            {displayTitle}
                            {!hideStar && <PageHeaderStar pageKey={pageKey} />}
                        </PageHeaderContainer.Title>
                    )}
                    {action && <PageHeaderContainer.Buttons>{action}</PageHeaderContainer.Buttons>}
                </PageHeaderContainer.CompactRow>
                {(description || learnMoreUrl) && (
                    <PageHeaderContainer.Description $hasDescription={!!description}>
                        <div>{description}</div>
                        {learnMoreUrl && (
                            <PageHeaderContainer.LearnMore href={learnMoreUrl} target='_blank' onClick={handleLearnMoreClick}>
                                {learnMoreLabel ? learnMoreLabel : 'Learn more'}
                            </PageHeaderContainer.LearnMore>
                        )}
                    </PageHeaderContainer.Description>
                )}
            </PageHeaderContainer.CompactWrap>
        );
    }

    return (
        <PageHeaderContainer>
            {((isBackVisible && !hideBreadcumb) || steps) && (
                <PageHeaderContainer.BackSteps>
                    {isBackVisible && !hideBreadcumb && (
                        <Breadcumb pageKey={pageKey} currentLabel={breadcumbCurrentLabel} extraItems={breadcumbExtraItems} onClick={onBackClick}>
                            {backLabel}
                        </Breadcumb>
                    )}
                    {steps && <PageHeaderContainer.Steps>{steps}</PageHeaderContainer.Steps>}
                </PageHeaderContainer.BackSteps>
            )}
            {(title || description) && (
                <PageHeaderContainer.TitleDescription>
                    {title && (
                        <PageHeaderContainer.Title>
                            {displayTitle}
                            {!hideStar && <PageHeaderStar pageKey={pageKey} />}
                        </PageHeaderContainer.Title>
                    )}
                    {(description || learnMoreUrl) && (
                        <PageHeaderContainer.Description $hasDescription={!!description}>
                            {description}
                            {learnMoreUrl && (
                                <>
                                    <PageHeaderContainer.LearnMore href={learnMoreUrl} target='_blank' onClick={handleLearnMoreClick}>
                                        {learnMoreLabel ? learnMoreLabel : 'Learn more'}
                                    </PageHeaderContainer.LearnMore>
                                </>
                            )}
                        </PageHeaderContainer.Description>
                    )}
                </PageHeaderContainer.TitleDescription>
            )}
            {action && <PageHeaderContainer.Buttons>{action}</PageHeaderContainer.Buttons>}
        </PageHeaderContainer>
    );
};

export const PageHeader = styled(_PageHeader)``;
